import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { listClients, listCompanies, listContacts, listLeads, listProposals, listProjects } from "../api/crm.ts";

interface Stats {
  companies: number;
  contacts: number;
  leads: number;
  proposals: number;
  clients: number;
  projects: number;
}

export function CrmOverviewPage() {
  const { token } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token == null) return;
    setLoading(true);

    Promise.all([
      listCompanies(token, { limit: 1 }),
      listContacts(token, { limit: 1 }),
      listLeads(token, { limit: 1 }),
      listProposals(token, { limit: 1 }),
      listClients(token, { limit: 1 }),
      listProjects(token, { limit: 1 }),
    ])
      .then(([companies, contacts, leads, proposals, clients, projects]) => {
        setStats({
          companies: companies.total,
          contacts: contacts.total,
          leads: leads.total,
          proposals: proposals.total,
          clients: clients.total,
          projects: projects.total,
        });
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load CRM stats"))
      .finally(() => setLoading(false));
  }, [token]);

  const cards = [
    { label: "Companies", count: stats?.companies ?? 0, to: "/crm/companies", color: "border-blue-500/30 bg-blue-900/10" },
    { label: "Contacts", count: stats?.contacts ?? 0, to: "/crm/contacts", color: "border-purple-500/30 bg-purple-900/10" },
    { label: "Leads", count: stats?.leads ?? 0, to: "/crm/leads", color: "border-yellow-500/30 bg-yellow-900/10" },
    { label: "Proposals", count: stats?.proposals ?? 0, to: "/crm/proposals", color: "border-orange-500/30 bg-orange-900/10" },
    { label: "Clients", count: stats?.clients ?? 0, to: "/crm/clients", color: "border-green-500/30 bg-green-900/10" },
    { label: "Projects", count: stats?.projects ?? 0, to: "/crm/projects", color: "border-teal-500/30 bg-teal-900/10" },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">CRM Overview</h1>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {cards.map((card) => (
            <Link
              key={card.to}
              to={card.to}
              className={`block rounded-lg border p-5 transition-all hover:brightness-110 ${card.color}`}
            >
              <p className="text-3xl font-bold text-white">{loading ? "—" : card.count.toLocaleString()}</p>
              <p className="text-sm text-gray-400 mt-1">{card.label}</p>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}
